/**
 * Layout-independent resolution of a keystroke to the US-QWERTY character that
 * physically produced it.
 *
 * CHARP barcode scanners (Zebra DS2208) are configured to emit **US English
 * (QWERTY)** HID scancodes — see [docs/barcode-scanners.md](../../../../docs/barcode-scanners.md).
 * The browser runs those scancodes through the *host* keyboard layout before it
 * hands us `KeyboardEvent.key`, so on a Belgian/French AZERTY machine the CHARP
 * QR payload `~https://go.charp.be/<devEUI>~~` arrives as `³httpsàvvgo…`: the
 * `~` prefix never matches and the scan is dropped silently.
 *
 * `KeyboardEvent.code` is the *physical* key position and is identical on every
 * layout, so resolving `code` + shift state against a US table gives back what
 * the scanner actually meant.
 */

/** `[unshifted, shifted]` US characters for one physical key. */
type UsKeyPair = readonly [unshifted: string, shifted: string];

/** Physical letter keys — handled separately because CapsLock applies to them. */
const LETTER_CODE = /^Key([A-Z])$/;

/**
 * Every non-letter printable key on a 104-key US keyboard.
 *
 * Numpad keys are unaffected by Shift, and NumLock needs no special handling:
 * with NumLock off `Numpad1` still reports `code: "Numpad1"` even though its
 * `key` becomes `"End"`.
 */
const US_KEY_MAP: Readonly<Record<string, UsKeyPair>> = {
  Digit0: ["0", ")"],
  Digit1: ["1", "!"],
  Digit2: ["2", "@"],
  Digit3: ["3", "#"],
  Digit4: ["4", "$"],
  Digit5: ["5", "%"],
  Digit6: ["6", "^"],
  Digit7: ["7", "&"],
  Digit8: ["8", "*"],
  Digit9: ["9", "("],
  Minus: ["-", "_"],
  Equal: ["=", "+"],
  BracketLeft: ["[", "{"],
  BracketRight: ["]", "}"],
  Backslash: ["\\", "|"],
  Semicolon: [";", ":"],
  Quote: ["'", '"'],
  Comma: [",", "<"],
  Period: [".", ">"],
  Slash: ["/", "?"],
  Backquote: ["`", "~"],
  Space: [" ", " "],
  Numpad0: ["0", "0"],
  Numpad1: ["1", "1"],
  Numpad2: ["2", "2"],
  Numpad3: ["3", "3"],
  Numpad4: ["4", "4"],
  Numpad5: ["5", "5"],
  Numpad6: ["6", "6"],
  Numpad7: ["7", "7"],
  Numpad8: ["8", "8"],
  Numpad9: ["9", "9"],
  NumpadDecimal: [".", "."],
  NumpadAdd: ["+", "+"],
  NumpadSubtract: ["-", "-"],
  NumpadMultiply: ["*", "*"],
  NumpadDivide: ["/", "/"],
} as const;

/** `getModifierState` is absent on hand-rolled synthetic events; treat it as "off". */
function modifierState(event: KeyboardEvent, key: string): boolean {
  return typeof event.getModifierState === "function" && event.getModifierState(key);
}

/**
 * Resolve a keydown event to the US-QWERTY character it represents, or `null`
 * when the event carries no character the scanner could have produced.
 *
 * Resolution order:
 * 1. `event.code` + Shift against the US table (handles every layout).
 * 2. Fallback to `event.key` when `code` is missing or unmapped — last resort,
 *    and only correct if the host happens to be US.
 *
 * CapsLock is ignored on **both** paths (OTO-162): a scanner presses Shift for
 * exactly the characters it wants upper-cased and cannot know the host has
 * CapsLock on. Note this makes casing CapsLock-independent everywhere, but only
 * tier 1 is *layout*-independent.
 *
 * `null` means "not scanner input": the caller should leave its buffer and
 * timers untouched and let the event through.
 */
export function usCharFromKeyEvent(event: KeyboardEvent): string | null {
  // Scanners never auto-repeat.
  if (event.repeat) return null;
  // Mid-IME-composition events carry no committed character.
  if (event.isComposing || event.keyCode === 229) return null;
  // Keyboard shortcuts (Cmd+K, Ctrl+C, Alt+…) are not scan input. Alt matters on its
  // own: without it, Alt+Shift+Backquote would still resolve to the "~" scan prefix.
  if (event.ctrlKey || event.metaKey || event.altKey) return null;
  // AltGr combos only exist on non-US layouts, so they cannot come from the scanner.
  // Windows reports AltGr as Ctrl+Alt (already caught); some browsers only set this.
  if (modifierState(event, "AltGraph")) return null;

  const shifted = event.shiftKey;
  const code = event.code;

  if (code) {
    const letter = LETTER_CODE.exec(code)?.[1];
    if (letter) {
      // CapsLock is deliberately ignored (OTO-162).
      //
      // A scanner presses Shift for every character it wants upper-cased, and it
      // has no idea the host has CapsLock engaged. Honouring CapsLock here — as
      // a host keyboard would — flips BOTH halves of the CHARP QR payload:
      // `https` (sent unshifted) becomes `HTTPS`, and the hex devEUI (sent WITH
      // Shift) becomes lower case. The URL prefix then fails to match and the
      // whole URL is passed on as the device token.
      //
      // This function decodes scanner input only; nothing here should behave
      // like a text field. See docs/adr/0011-barcode-scanner-layout-independent-input.md.
      return shifted ? letter : letter.toLowerCase();
    }

    const pair = US_KEY_MAP[code];
    if (pair) return pair[shifted ? 1 : 0];
  }

  // Tier 2: no usable `code`. Non-printable keys ("Enter", "Shift", "Dead", …)
  // have a multi-character `key` and drop out here.
  if (event.key.length !== 1) return null;

  // The OS has already folded CapsLock into `event.key`, so returning it as-is
  // would reintroduce on this path exactly the casing bug the `code` path above
  // avoids. CapsLock's effect on a letter is reversible — it only ever swaps
  // case — so recompute from Shift alone and the "CapsLock is ignored"
  // guarantee holds however the event was resolved. Non-letters are untouched:
  // CapsLock never applied to them in the first place.
  //
  // This does NOT make the fallback layout-independent; it is still only
  // correct on a US host. It just stops CapsLock adding a second error.
  if (/^\p{L}$/u.test(event.key)) {
    return shifted ? event.key.toUpperCase() : event.key.toLowerCase();
  }
  return event.key;
}
